import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { api, clearToken, getToken, setToken } from './api';
import type { Partner, Role, User } from './types';

interface AuthState {
  user: User | null;
  ready: boolean;
  owners: Partner[];
  /** Global gewählter Eigentümer; leer = alle. */
  ownerId: string;
  setOwnerId: (id: string) => void;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  can: (...roles: Role[]) => boolean;
  /** Pfade, die durch ein abgeschaltetes Modul verborgen sind. */
  verborgen: Set<string>;
  /** Inhaltsbereiche, die die Rolle des Nutzers sehen darf (konfigurierbar). */
  bereiche: Set<string>;
  /** Einzelne Menüpunkte (Routen), die für die Rolle ausgeblendet sind. */
  menuVersteckt: Set<string>;
}

const OWNER_KEY = 'wawi.owner';
const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [ready, setReady] = useState(false);
  const [owners, setOwners] = useState<Partner[]>([]);
  const [verborgen, setVerborgen] = useState<Set<string>>(new Set());
  const [bereiche, setBereiche] = useState<Set<string>>(new Set());
  const [menuVersteckt, setMenuVersteckt] = useState<Set<string>>(new Set());
  const [ownerId, setOwnerIdState] = useState(() => localStorage.getItem(OWNER_KEY) ?? '');

  /**
   * Eigentümerliste holen und die gespeicherte Auswahl damit abgleichen.
   *
   * Der Abgleich ist der wichtigere Teil. Die Auswahl überlebt im Browser,
   * die Kennung dahinter nicht unbedingt: Ein Auftraggeber wird gelöscht oder
   * inaktiv gesetzt, ein Benutzer bekommt andere Rechte, eine Testdatenbank
   * wird neu aufgesetzt. Dann zeigt das Auswahlfeld weiter "Alle Eigentümer" —
   * denn einen Wert, den es nicht kennt, stellt es gar nicht dar — während der
   * Server nach einer toten Kennung filtert und jede Seite leer bleibt.
   *
   * Ein Filter, den niemand sieht, ist schlimmer als ein sichtbarer.
   */
  const loadOwners = useCallback(async (gebundenAn?: string | null) => {
    try {
      // Der Modulstand entscheidet, was im Menue erscheint. Faellt der Abruf
      // aus, wird nichts verborgen - lieber zu viel zeigen als eine Anwendung,
      // die nach einem Netzfehler halb leer aussieht.
      void api<{ module: { pfade: string[]; aktiv: boolean; gebucht: boolean }[] }>(
        '/api/system/module',
      )
        .then((stand) => {
          const aus = new Set<string>();
          for (const modul of stand.module) {
            if (!modul.aktiv || !modul.gebucht) modul.pfade.forEach((pfad) => aus.add(pfad));
          }
          setVerborgen(aus);
        })
        .catch(() => setVerborgen(new Set()));

      const liste = await api<Partner[]>('/api/partners/owners');
      setOwners(liste);
      setOwnerIdState((aktuell) => {
        if (gebundenAn) return gebundenAn;
        if (aktuell && !liste.some((eigentuemer) => eigentuemer.id === aktuell)) {
          localStorage.removeItem(OWNER_KEY);
          return '';
        }
        return aktuell;
      });
    } catch {
      setOwners([]);
    }
  }, []);

  useEffect(() => {
    if (!getToken()) {
      setReady(true);
      return;
    }
    api<{ user: User; bereiche: string[]; menuVersteckt?: string[] }>('/api/auth/me')
      .then(async ({ user: me, bereiche: b, menuVersteckt: mv }) => {
        setUser(me);
        setBereiche(new Set(b));
        setMenuVersteckt(new Set(mv ?? []));
        await loadOwners(me.ownerId);
      })
      .catch(() => clearToken())
      .finally(() => setReady(true));
  }, [loadOwners]);

  const setOwnerId = useCallback((id: string) => {
    setOwnerIdState(id);
    localStorage.setItem(OWNER_KEY, id);
  }, []);

  const login = useCallback(
    async (email: string, password: string) => {
      const result = await api<{ token: string; user: User; bereiche: string[]; menuVersteckt?: string[] }>('/api/auth/login', {
        method: 'POST',
        body: { email, password },
      });
      setToken(result.token);
      setUser(result.user);
      setBereiche(new Set(result.bereiche));
      setMenuVersteckt(new Set(result.menuVersteckt ?? []));
      // Auftraggeber sind fest auf ihre eigene Ware gebunden.
      if (result.user.ownerId) setOwnerId(result.user.ownerId);
      await loadOwners(result.user.ownerId);
    },
    [loadOwners, setOwnerId],
  );

  const logout = useCallback(() => {
    clearToken();
    setUser(null);
    window.location.href = '/login';
  }, []);

  const can = useCallback(
    (...roles: Role[]) => (user ? roles.includes(user.role) : false),
    [user],
  );

  const value = useMemo(
    () => ({ user, ready, owners, ownerId, setOwnerId, login, logout, can, verborgen, bereiche, menuVersteckt }),
    [user, ready, owners, ownerId, setOwnerId, login, logout, can, verborgen, bereiche, menuVersteckt],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth ausserhalb von AuthProvider');
  return context;
}

export const ROLE_LABEL: Record<Role, string> = {
  admin: 'Administrator',
  kaufmann: 'Kaufmännisch',
  pruefer: 'Betriebsprüfung (nur lesen)',
  disponent: 'Disposition',
  schichtleiter: 'Schichtleitung',
  lagerist: 'Lager',
  auftraggeber: 'Auftraggeber',
};
